// Slice H2 — FTMO Intraday Compliant (curated template v1).
//
// Week 2 schema fix:
//   • SL/TP/entry/risk_rule field names fixed to engine canonical
//     (same fixes as the EMA Pullback template).
//   • time_filter block: switched from `sessionStart`/`sessionEnd`/
//     `timezone`/`weekdaysOnly` → engine-canonical `filterType: 'time_range'`
//     + `startTime` + `endTime` + `timezone` + `noWeekends`. The pre-fix
//     fields were silently ignored by `evalTimeFilter` → the strategy
//     traded 24/7 regardless of the documented EU/US session window.
//
// PROP-FIRM STARTER TEMPLATE — NOT the full FTMO sweep+failure
// production strategy. That strategy lives in
// `scripts/seed-ftmo-sweep-template.mjs` (Pine v5 parity, 100+ blocks,
// custom sweep-failure detection logic). H2's prop-firm template is a
// SIMPLER intraday entry strategy with the full FTMO ruleset attached,
// suitable as a starting point that users can refine.

import { BlockType, type Block } from '../types';
import {
  type CuratedTemplate,
  templateBlockMetadata,
  namespacedBlockId,
} from './types';
import {
  maxRiskPerTradeParameters,
} from './canonicalRiskRuleBodies';

const SLUG = 'propfirm-intraday-ftmo';
const id = (name: string) => namespacedBlockId(SLUG, name);
const meta = (desc: string) => templateBlockMetadata(desc);

const blocks: Block[] = [
  // ── Strategy config with FULL FTMO ruleset attached ───────────────
  {
    id: id('config'),
    type: BlockType.STRATEGY_CONFIG,
    data: {
      name: 'Strategy config (FTMO compliant)',
      initial_capital: 100_000,            // Typical FTMO challenge size
      commission_value: 0.06,
      commission_type: 'percentage',
      slippage_value: 0.02,
      slippage_type: 'percentage',
      // S65 (2026-06-06) — normalized to false. See resolveEffectiveConfig.ts
      // for the rationale + trade-off; this matches the new product-wide
      // default. Users can tick it back on in the cfg block.
      process_orders_on_close: false,
      // Engine reads propFirm + propFirmPreset at evaluation time.
      // Halt-rules fire BEFORE entries / risk-sizing, so the strategy
      // logic below doesn't have to re-implement them.
      propFirm: {
        provider: 'ftmo',
        enforcementMode: 'gate',           // halts new entries on breach
        maxDailyLossPct: 5.0,
        maxTotalLossPct: 10.0,
        trailingDrawdownPct: 10.0,
        maxTradesPerDay: 3,
        dailyProfitLockPct: 1.0,
      },
      propFirmPreset: 'ftmo_2step_challenge',
    } as Block['data'],
    position: { x: 50, y: 320 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('100k FTMO 2-step challenge config. Engine enforces the ruleset as halts.'),
  },

  {
    id: id('price'),
    type: BlockType.PRICE_DATA,
    data: { name: 'Price data' } as Block['data'],
    position: { x: 50, y: 80 },
    connections: { inputs: [], outputs: [id('ema-fast'), id('ema-slow'), id('rsi')] },
    metadata: meta('Source candle feed.'),
  },

  // ── Indicators ────────────────────────────────────────────────────
  {
    id: id('ema-fast'),
    type: BlockType.MOVING_AVERAGE,
    data: {
      name: 'Fast EMA (20)',
      period: 20,
      source: 'close',
      indicator: 'ema',
      smoothing: 2,
    } as Block['data'],
    position: { x: 230, y: 40 },
    connections: { inputs: [id('price')], outputs: [id('expr-trend')] },
    metadata: meta('Trend confirmation — short side.'),
  },

  {
    id: id('ema-slow'),
    type: BlockType.MOVING_AVERAGE,
    data: {
      name: 'Slow EMA (50)',
      period: 50,
      source: 'close',
      indicator: 'ema',
      smoothing: 2,
    } as Block['data'],
    position: { x: 230, y: 110 },
    connections: { inputs: [id('price')], outputs: [id('expr-trend')] },
    metadata: meta('Trend confirmation — long-side anchor.'),
  },

  {
    id: id('rsi'),
    type: BlockType.MOMENTUM_OSCILLATOR,
    data: {
      name: 'RSI (14)',
      indicator: 'rsi',
      period: 14,
      source: 'close',
    } as Block['data'],
    position: { x: 230, y: 180 },
    connections: { inputs: [id('price')], outputs: [id('expr-momentum')] },
    metadata: meta('Momentum confirmation — RSI > 50 for longs.'),
  },

  // ── Session filter (Week 2: canonical time_filter shape) ──────────
  // Engine reads `filterType: 'time_range'` + `startTime` + `endTime` +
  // `timezone` + `noWeekends` (per `engine/graph.ts:evalTimeFilter`).
  // Pre-fix the sessionStart/sessionEnd fields were silently ignored.
  {
    id: id('session-filter'),
    type: BlockType.TIME_FILTER,
    data: {
      name: 'EU/US session (13:00-17:00 UTC)',
      filterType: 'time_range',
      startTime: '13:00',
      endTime: '17:00',
      timezone: 'UTC',
      noWeekends: true,
    } as Block['data'],
    position: { x: 230, y: 260 },
    connections: { inputs: [], outputs: [id('and-entry')] },
    metadata: meta('Highest-liquidity 4-hour window — best for FTMO compliance.'),
  },

  // ── Entry conditions ─────────────────────────────────────────────
  {
    id: id('expr-trend'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'Fast > Slow (uptrend)',
      leftSource: 'input',
      leftInputBlockId: id('ema-fast'),
      rightSource: 'input',
      rightInputBlockId: id('ema-slow'),
      operator: '>',
    } as Block['data'],
    position: { x: 430, y: 75 },
    connections: { inputs: [id('ema-fast'), id('ema-slow')], outputs: [id('and-entry')] },
    metadata: meta('Trend confirmation.'),
  },

  {
    id: id('expr-momentum'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'RSI > 50 (momentum)',
      leftSource: 'input',
      leftInputBlockId: id('rsi'),
      rightSource: 'static',
      rightValue: 50,
      operator: '>',
    } as Block['data'],
    position: { x: 430, y: 180 },
    connections: { inputs: [id('rsi')], outputs: [id('and-entry')] },
    metadata: meta('Momentum agrees with trend.'),
  },

  {
    id: id('and-entry'),
    type: BlockType.LOGIC_GATE,
    data: {
      name: 'Entry AND',
      gateType: 'AND',
    } as Block['data'],
    position: { x: 630, y: 130 },
    connections: {
      inputs: [id('expr-trend'), id('expr-momentum'), id('session-filter')],
      outputs: [id('entry')],
    },
    metadata: meta('Trend + momentum + session all required.'),
  },

  // ── Terminals ────────────────────────────────────────────────────
  {
    id: id('entry'),
    type: BlockType.ENTRY,
    data: {
      name: 'Long entry (FTMO-compliant)',
      direction: 'long',
      entryType: 'market',
      logic: 'AND',
      useStrategyDefaults: false,
    } as Block['data'],
    position: { x: 830, y: 130 },
    connections: { inputs: [id('and-entry')], outputs: [] },
    metadata: meta('Market long; canonical risk rule + propFirm halts apply.'),
  },

  {
    id: id('exit'),
    type: BlockType.EXIT,
    data: {
      name: 'Exit on trend break',
    } as Block['data'],
    position: { x: 830, y: 220 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Exit when fast EMA crosses below slow.'),
  },

  {
    id: id('sl'),
    type: BlockType.STOP_LOSS,
    data: {
      name: 'Stop loss (1.5%)',
      stopType: 'percentage',
      stopValue: 1.5,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
      exitMode: 'full',
    } as Block['data'],
    position: { x: 830, y: 300 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Tight — FTMO drawdown gates make wider stops too risky.'),
  },

  {
    id: id('tp'),
    type: BlockType.TAKE_PROFIT,
    data: {
      name: 'Take profit (1:1.67 risk-reward)',
      tpType: 'risk_reward',
      riskRewardRatio: 1.67,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 830, y: 380 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Modest target compatible with the 3-trades-per-day cap.'),
  },

  // ── Risk-rule blocks (canonical) ─────────────────────────────────
  // The propFirm config on strategy_config handles daily-loss / total-loss
  // / trailing-DD / max-trades / profit-lock automatically. The
  // max_risk_per_trade rule below is a defense-in-depth pin so the
  // sizing-resize logic caps per-trade risk at 1% of equity even if a
  // user edits the entry block.
  {
    id: id('risk-max-per-trade'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Max risk per trade (1%)',
      blockType: 'risk_rule',
      // TSL migration: the vestigial JS body is removed. The engine enforces
      // this rule via the `__canonical` marker on the parameters (trusted-TS
      // dispatch in riskRules.ts), so no executable strategy code is carried.
      parameters: maxRiskPerTradeParameters(1.0, 'resize'),
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 50, y: 480 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Per-trade risk capped at 1% — FTMO-safe defense in depth.'),
  },
];

export const PROPFIRM_INTRADAY_FTMO: CuratedTemplate = {
  slug: SLUG,
  name: 'FTMO Intraday Compliant (v1)',
  family: 'prop-firm',
  bestFor: 'Intraday long entries during EU/US overlap with the full FTMO ruleset attached.',
  detail:
    'A starter template for FTMO 2-step challenge accounts. Confirms direction via EMA(20)/EMA(50), ' +
    'requires RSI > 50 momentum, and gates entries to the high-liquidity EU/US session overlap ' +
    '(13:00-17:00 UTC). The strategy_config block embeds the full FTMO ruleset (5% max daily loss, ' +
    '10% max total loss, 10% trailing DD, 3 trades/day, 1% daily profit lock), so the engine halts ' +
    'new entries on breach automatically — no need to re-implement the rules in the graph. NOT the ' +
    'full Pine-parity FTMO sweep+failure strategy (that lives in the seed-script; this is a simpler ' +
    'starting point).',
  assetGuidance:
    'Forex majors or crypto futures. Intraday timeframes only — the 3-trades-per-day cap and ' +
    'session filter assume sub-1h bars.',
  timeframeGuidance: ['5m', '15m'],
  riskNotes: [
    'Yellow validation common — FTMO rules constrain the strategy enough that "robust" is rare',
    'Session filter blocks bars outside 13:00-17:00 UTC; expect lower trade count than free-trading templates',
    'Modify the propFirm preset for other prop firms (NOT a Goat-compliant template — that\'s deferred)',
    'Long-only v1 — symmetric short with the same ruleset is a natural v2 extension',
  ],
  expectedValidation:
    'Yellow expected; green possible on strong-trending samples; red+gate_failed if drawdown ramps ' +
    'past the 10% trailing limit during a bad fold.',
  blocks,
  version: 'v1',
  propFirm: {
    provider: 'ftmo',
    preset: 'ftmo_2step_challenge',
  },
};
